import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import client from "../contentful";
import ProductCard from "./ProductCard";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { CONTENT_TYPES } from "../constants/contentTypes";
import { useLocation } from "react-router-dom";
import ContactModal from "./ContactModal";

/**
 * A simple skeleton placeholder grid.
 * You can customize the number of items if desired.
 */
function SkeletonGrid({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 w-full max-w-7xl mx-auto">
      {Array.from({ length: count }).map((_, index) => (
        <motion.div
          key={index}
          className="bg-[#F2F2F2] rounded-lg p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Image placeholder */}
          <div className="w-full h-64 bg-[#E5E5E5] rounded-lg mb-4 shimmer" />
          {/* Title placeholder */}
          <div className="h-5 w-3/4 bg-[#E5E5E5] rounded mb-2 shimmer" />
          {/* Subtitle placeholder */}
          <div className="h-4 w-1/2 bg-[#E5E5E5] rounded mb-4 shimmer" />
          {/* Button placeholder */}
          <div className="h-8 w-1/3 bg-[#E5E5E5] rounded-full shimmer" />
        </motion.div>
      ))}
    </div>
  );
}

const ProductsSection = ({ 
  contentType = CONTENT_TYPES.HOUSEHOLD, 
  title = 'Our Products',
  isProfessional = false,
  showQuoteButton = false
}) => {
  const location = useLocation();
  const isProductPage = location.pathname === '/industrial' || 
                       location.pathname === '/household' || 
                       location.pathname === '/household/home' ||
                       location.pathname === '/industrial/home';
  
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const productsPerPage = isProfessional ? 15 : 6; // Show all products for industrial, paginate for household

  useEffect(() => {
    setIsLoading(true);
    client
      .getEntries({ content_type: contentType })
      .then((response) => {
        setProducts(response.items);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
        setIsLoading(false);
      });
  }, [contentType]);

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = isProfessional ? products : products.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(products.length / productsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  const renderPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => paginate(i)}
          className={`mx-1 px-3 py-1 rounded-full ${
            currentPage === i 
              ? "bg-[#D3242A] text-white" 
              : "bg-gray-200 hover:bg-gray-300 text-gray-700"
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <section id="our-products" className="bg-white py-16 w-full">
      <div className="px-4 sm:px-6 lg:px-8">
        <motion.h2
          className="text-5xl font-bold text-[#D3242A] mb-12 uppercase tracking-wide text-center"
          style={{ fontFamily: "Geogrotesque, sans-serif" }}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          {title}
        </motion.h2>

        {showQuoteButton && (
          <div className="text-center mb-8">
            <button
              type="button"
              onClick={() => setIsContactModalOpen(true)}
              className="inline-block bg-[#D3242A] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#B31E24] transition-colors"
            >
              Request a Quote
            </button>
          </div>
        )}

        <div className="flex justify-center w-full">
          <AnimatePresence mode="wait">
            {isLoading ? (
              <SkeletonGrid count={6} />
            ) : products.length === 0 ? (
              <motion.div 
                className="text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <p className="text-black text-lg">No products found!</p>
              </motion.div>
            ) : (
              <motion.div
                className="w-full max-w-[1400px] mx-auto px-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className={`grid gap-4 w-full ${
                  isProfessional 
                    ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
                    : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                }`}>
                  {currentProducts.map((product, index) => (
                    <motion.div
                      key={product.sys.id || index}
                      initial={{ 
                        opacity: 0,
                        y: 30
                      }}
                      animate={{ 
                        opacity: 1,
                        y: 0
                      }}
                      transition={{ 
                        duration: 0.2,
                        ease: [0.16, 1, 0.3, 1],
                        delay: index * 0.1
                      }}
                      whileHover={{ 
                        scale: 1.02,
                        transition: { 
                          duration: 0.15,
                          ease: [0.16, 1, 0.3, 1]
                        }
                      }}
                      className="w-full max-w-[600px] mx-auto"
                    >
                      <ProductCard 
                        product={product}
                        sequence={index}
                        isProfessional={isProfessional}
                        onRequestQuote={() => setIsContactModalOpen(true)}
                      />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </section>
  );
};

export default ProductsSection;